import {inject, Injectable} from '@angular/core';
import { Subject} from "rxjs";
import {CommunicationCenterService} from "@modules/communication-center";
import {tap} from "rxjs/operators";
import {UserDataEntity} from "@modules/authentication";

type PremiumPathConfig = {
    whitelist: RegExp[];
    blacklist?: never;
} | {
    whitelist?: never;
    blacklist: RegExp[];
}

@Injectable({
    providedIn: 'root'
})
export class FreemiumPremiumService {

    private actualMode: 'freemium' | 'premium' = 'freemium'
    private freemiumPremiumFeatureEnabled = false
    private premiumPathConfig: PremiumPathConfig = { blacklist: []};
    private unauthorizedAccess$ = new Subject<string>();
    userData: UserDataEntity | null = null;
    private communicationCenter = inject(CommunicationCenterService);
    constructor() {
        this.communicationCenter
            .getRoom('authentication')
            .getSubject('userData')
            .pipe(
                tap((userData: UserDataEntity) => {
                    if (!!userData) {
                        this.userData = userData;
                    }
                }),
            )
            .subscribe();
        this.communicationCenter
            .getRoom('freemium-premium')
            .getSubject('freemiumPremiumFeatureEnabled')
            .pipe(
                tap((enabled: boolean) => this.freemiumPremiumFeatureEnabled = enabled),
            )
            .subscribe();

        this.communicationCenter
            .getRoom('freemium-premium')
            .getSubject('freemiumPremiumMode')
            .subscribe((callbackSubject: Subject<{freemium: boolean, premium: boolean}>) =>
                callbackSubject.next({freemium: this.isFreemiumMode(), premium: this.isPremiumMode()})
            );
    }
    public onUnauthorizedAccess() {
        return this.unauthorizedAccess$.asObservable();
    }

    public forceTriggerUnauthorizedAccess(url: string): void {
        this.triggerUnauthorizedAccess(url);
    }

    isFreemiumPremiumFeatureEnabled(): boolean {
        // Logic to determine if the Freemium Premium feature is enabled
        // This could be based on user settings, feature flags, etc.
        // For now, we return true to indicate the feature is enabled.
        return this.freemiumPremiumFeatureEnabled;
    }

    isFreemiumMode(): boolean {
        return this.isFreemiumPremiumFeatureEnabled() && this.actualMode === 'freemium';
    }


    isPremiumMode(): boolean {
        return this.isFreemiumPremiumFeatureEnabled() && this.actualMode === 'premium';
    }

    isRouteAuthorized(url: string) {
        // Logic to check if the route is authorized based on the current mode
        // This could involve checking against a list of authorized routes for each mode
        // For now, we assume all routes are authorized in premium mode and only specific ones in premium mode
        if (this.isPremiumMode()) {
            return true; // All routes are accessible in premium mode
        } else if (this.isFreemiumMode()) {
            // Check if the route is authorized for premium users
            if (this.premiumPathConfig.whitelist) {
                return this.premiumPathConfig.whitelist.some(pattern => pattern.test(url));
            } else if (this.premiumPathConfig.blacklist) {
                return !this.premiumPathConfig.blacklist.some(pattern => pattern.test(url));
            }
        }

        return true;
    }

    triggerUnauthorizedAccess(url: string) {
        // Logic to handle unauthorized access, such as redirecting to a login page or showing an error message
        console.warn(`Unauthorized access attempt to: ${url}`);
        // Open a modal, redirect, or log the attempt
        this.unauthorizedAccess$.next(url);
    }

    setMode(mode: 'freemium' | 'premium' | 'disabled', config?: { pathList?: PremiumPathConfig }) {
        if (mode === 'freemium' || mode === 'premium') {
            if ('whitelist' in config?.pathList && 'blacklist' in config?.pathList) {
                throw new Error('Invalid configuration: cannot have both whitelist and blacklist in premiumPathList');
            }

            this.premiumPathConfig = config.pathList;

            this.actualMode = mode;
            this.freemiumPremiumFeatureEnabled = true;
        } else if (mode === 'disabled') {
            this.actualMode = 'freemium'; // Default to freemium when disabled
            this.freemiumPremiumFeatureEnabled = false;
            this.premiumPathConfig = { blacklist : []};
        }
    }

    get matchedUrls(): { [key: string]: string } {
    return {
            '/lessons/list/search?usage=4056' : 'individual_lessons',
            '/lessons/list/lessons' : 'editor',
            '#navigation.dictionary' : 'dictionary',
            '#navigation.language_laboratory' : 'dictionary', // 'language_lab',
            '/groups/list/groups' : 'groups',
            '/groups/list/workgroups' : 'groups', // 'workgroups',
            '/groups/list/learners' : 'groups', // 'learners',
            '/followed/tab' : 'followed',
            '/graph-humanum/multi' : 'followed', // 'graph',
            '/corpus/11057' : 'media_co',
            [`/corpus/user/${this.userData.id}`] : 'media_co', // 'mediatheque',
            '/notepad/list' : 'notepad',
            '/mindmap/list' : 'mindmap',
            '/bd-tool/list' : 'bd-tool',
            '/code/gleamcode/list' : 'gleamcode',
            '/code/gamecode/list' : 'gamecode',
            '/further/ressources-partenaires-ilove' : 'ressources-partenaires-ilove',
            '/further/ressources-supplementaires' : 'ressources-supplementaires',
            '#navigation.learner_app' : 'learner_app',
            'lesson-edition/editor/': 'editor',
        }
    }
}